/**
 * A project's incidents (docs/plans/2026-10-hermes-adoption.md, item 3): a routine, a cloud agent, a
 * node or an automated member failing the same way again and again is one row with its count, not a
 * row per failure. Acknowledge keeps it quiet for a day; Close ends it until the error returns.
 */

import { INCIDENT_KIND_TEXT, INCIDENT_STATE_TEXT, type Incident, type IncidentState } from '@aioe/shared';
import { CircleExclamation } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { useState } from 'react';

import { routes } from '../api/client.ts';
import { timeAgo, useLoad } from '../hooks.ts';
import { Empty, ErrorNote, ListBlock, ListGroup, ListRow, Stat, StatusMark } from './ui.tsx';
import { VIEW_TONES } from './ui/Art.tsx';
import { RowMenu } from './ui/RowMenu.tsx';

const MARK: Record<IncidentState, 'error' | 'idle' | 'disabled'> = { open: 'error', acknowledged: 'idle', closed: 'disabled' };

export function ProjectIncidents({ projectId, canWrite }: { projectId: string; canWrite: boolean }) {
  const tone = VIEW_TONES.projects!;
  const [state, setState] = useState<'unclosed' | 'all'>('unclosed');
  const data = useLoad(() => routes.projectIncidents(projectId, 'all'), [projectId], 30_000);
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  const all = data.data?.incidents ?? [];
  const open = all.filter((i) => i.state === 'open');
  const shown = all.filter((i) => state === 'all' || i.state !== 'closed');
  const failures = all.filter((i) => i.state !== 'closed').reduce((n, i) => n + i.count, 0);
  const act = async (i: Incident, verb: 'acknowledge' | 'close') => {
    setBusy(`${i.id}:${verb}`);
    setError(undefined);
    try {
      await (verb === 'acknowledge' ? routes.acknowledgeIncident(projectId, i.id) : routes.closeIncident(projectId, i.id));
      data.reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(undefined);
    }
  };
  return (
    <>
      <div className="art-stats">
        <Stat tone={tone} icon={<CircleExclamation />} label="Open incidents" value={data.data ? open.length : undefined} detail={open.length ? 'Something keeps failing' : 'Nothing is failing repeatedly'} needsYou={open.length > 0} />
        <Stat tone={tone} icon={<CircleExclamation />} label="Failures folded" value={data.data ? failures : undefined} detail="Across the incidents still open or acknowledged" />
        <Stat tone={tone} icon={<CircleExclamation />} label="Acknowledged" value={data.data ? all.filter((i) => i.state === 'acknowledged').length : undefined} detail="Quiet for a day, then raised again if it repeats" />
      </div>
      <ListGroup
        title="Incidents"
        count={shown.length || undefined}
        description="The same failure again is one row. Acknowledge keeps it quiet for a day; Close ends it until the error returns."
        actions={
          <Segment aria-label="Which incidents" size="sm" selectedKey={state} onSelectionChange={(k) => setState(String(k) as typeof state)} className="w-fit">
            <Segment.Item id="unclosed">Open</Segment.Item>
            <Segment.Item id="all">All</Segment.Item>
          </Segment>
        }
        empty={data.data && !shown.length && !error && !data.error ? <Empty tone={tone} icon={<CircleExclamation />} compact title={state === 'all' ? 'No incidents' : 'Nothing is failing repeatedly'} body="A routine, a cloud agent, a node or an automated member that fails the same way again shows here as one incident, with its count." /> : undefined}
      >
        {error || data.error ? (
          <ListBlock>
            <ErrorNote error={error ?? data.error} />
          </ListBlock>
        ) : null}
        {!data.data && !data.error ? <ListBlock className="text-sm text-muted">Reading the incidents…</ListBlock> : null}
        {shown.map((i) => (
          <ListRow
            key={i.id}
            title={i.subjectName}
            description={
              <>
                {INCIDENT_KIND_TEXT[i.kind]} · {i.count === 1 ? `once, ${timeAgo(i.firstAt)}` : `${i.count.toLocaleString()} times, first ${timeAgo(i.firstAt)}, last ${timeAgo(i.lastAt)}`}
                {i.state === 'acknowledged' && i.acknowledgedAt ? ` · acknowledged ${timeAgo(i.acknowledgedAt)}` : ''}
                {i.state === 'closed' && i.closedAt ? ` · closed ${timeAgo(i.closedAt)}` : ''}
                <span className="mono mt-1 block break-words">{i.error}</span>
              </>
            }
            status={<StatusMark state={MARK[i.state]} label={busy?.startsWith(`${i.id}:`) ? 'Saving…' : INCIDENT_STATE_TEXT[i.state]} />}
            compactControl
            control={
              canWrite && i.state !== 'closed' ? (
                <RowMenu
                  label={`More for ${i.subjectName}`}
                  items={[i.state === 'open' && { id: 'acknowledge', label: 'Acknowledge', disabled: Boolean(busy) }, { id: 'close', label: 'Close', disabled: Boolean(busy) }]}
                  onAction={(verb) => void act(i, verb as 'acknowledge' | 'close')}
                />
              ) : undefined
            }
          />
        ))}
      </ListGroup>
    </>
  );
}
